// Los dos formularios de Excepciones: alta y resolución.
//
// Los dos validan con zod ANTES de enviar. El backend también valida, pero descubrir que la
// justificación era obligatoria mediante un 400, después de redactar el caso completo, es una
// forma cara de enterarse — y en un flujo de auditoría el operador suele escribir bastante.
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, Modal, Select, type SelectOption } from '@/kit';
import type { ExceptionDetail } from '@/api/services/retail-wizard/backoffice';
import {
    createExceptionSchema,
    EMPTY_CREATE_FORM,
    EXCEPTION_TYPES,
    MIN_JUSTIFICATION,
    MIN_RESOLUTION,
    resolveExceptionSchema,
    type CreateExceptionForm,
    type ResolveExceptionForm,
} from '../lib/excepcionesSchema';

const FIELD_LABEL = 'mb-1 block text-[11px] font-medium text-foreground';
const TEXTAREA =
    'w-full rounded-md border border-input bg-transparent px-2.5 py-2 text-[12px] outline-none focus-visible:border-ring';
const ERROR = 'mt-1 text-[11px] text-destructive';

export function CreateExceptionModal({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting,
    storeOptions,
    storesUnavailable,
    prefill,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (values: CreateExceptionForm) => void;
    isSubmitting: boolean;
    /**
     * Valores con los que abre el formulario — lo que usa «Duplicar» de un caso cerrado.
     *
     * Es el `openLevantar(pref)` del mockup, que también entra por el deep link `?venta=` desde la
     * pantalla de Ventas. Se siembra con `reset()` al abrir, no como `defaultValues`: el modal vive
     * montado y `defaultValues` solo se lee en el primer render.
     */
    prefill?: Partial<CreateExceptionForm>;
    /**
     * 🔴 Las tiendas REALES del padrón. Antes eran siete constantes inventadas (`ST-01 Centro
     * CDMX`…) que no existen en el silo: **todo submit moría en 404 `STORE_NOT_IN_RETAILER`**, o
     * sea que el formulario no podía crear ni una sola excepción. Y contradecía de frente la regla
     * escrita en el facade del backoffice: «un dato de muestra en una pantalla de operación es peor
     * que una pantalla vacía».
     */
    storeOptions: SelectOption[];
    storesUnavailable: boolean;
}) {
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<CreateExceptionForm>({
        resolver: zodResolver(createExceptionSchema),
        defaultValues: EMPTY_CREATE_FORM,
    });

    // Siembra los valores de «Duplicar» cada vez que el modal se abre. `defaultValues` no alcanza:
    // el componente queda montado entre aperturas y esos solo se leen una vez.
    useEffect(() => {
        if (isOpen) reset({ ...EMPTY_CREATE_FORM, ...prefill });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `prefill` se re-crea en cada render del padre
    }, [isOpen, reset]);

    const close = () => {
        reset();
        onClose();
    };

    const justification = watch('justification');

    return (
        <Modal isOpen={isOpen} onClose={close} title="Levantar una excepción" size="lg">
            <form
                // `handleSubmit` devuelve una promesa y el `onSubmit` del form espera void: se
                // envuelve con `void` en vez de dejar una promesa flotando sin manejar.
                onSubmit={(event) => {
                    void handleSubmit((values) => {
                        onSubmit(values);
                        reset();
                    })(event);
                }}
                className="flex flex-col gap-3"
            >
                <div className="grid grid-cols-2 gap-3">
                    {/* El `label` del kit ya cablea `htmlFor` contra el trigger. Antes se usaba un
                        `<span>` suelto y los dos selects quedaban sin nombre accesible. */}
                    <Select
                        id="exc-type"
                        label="Tipo"
                        required
                        value={watch('type')}
                        onChange={(v) => {
                            setValue('type', v as CreateExceptionForm['type']);
                        }}
                        options={EXCEPTION_TYPES.map((k) => ({ value: k.value, label: k.label }))}
                    />
                    <Select
                        id="exc-store"
                        label="Tienda"
                        value={watch('storeId')}
                        onChange={(v) => {
                            setValue('storeId', v, { shouldValidate: true });
                        }}
                        options={storeOptions}
                        placeholder={
                            storeOptions.length === 0
                                ? 'Sin tiendas disponibles'
                                : 'Elige la tienda'
                        }
                        disabled={storeOptions.length === 0}
                        error={errors.storeId?.message}
                    />
                </div>

                {storesUnavailable ? (
                    <p className="rounded-md border border-warning-soft-border bg-warning-soft p-2 text-[11px] text-warning">
                        No se pudo cargar el padrón de tiendas. Puedes levantar la excepción
                        ligándola a una venta, o vuelve a intentarlo en un momento.
                    </p>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={FIELD_LABEL} htmlFor="exc-sale">
                            Venta relacionada{' '}
                            <span className="text-muted-foreground">
                                (opcional si elegiste tienda)
                            </span>
                        </label>
                        <Input
                            id="exc-sale"
                            {...register('saleId')}
                            placeholder="Identificador de la venta"
                        />
                        {errors.saleId ? <p className={ERROR}>{errors.saleId.message}</p> : null}
                    </div>
                    <div>
                        <label className={FIELD_LABEL} htmlFor="exc-impact">
                            Impacto en pesos{' '}
                            <span className="text-muted-foreground">(negativo si devuelve)</span>
                        </label>
                        <Input id="exc-impact" {...register('impactPesos')} placeholder="0" />
                        {errors.impactPesos ? (
                            <p className={ERROR}>{errors.impactPesos.message}</p>
                        ) : null}
                    </div>
                </div>

                {/* El «Solicitante» del mockup, que faltaba entero. Sin él la bandeja atribuye TODA
                    excepción al Admin que la capturó y se pierde quién originó el caso. */}
                <div>
                    <label className={FIELD_LABEL} htmlFor="exc-requested-by">
                        Solicitante{' '}
                        <span className="text-muted-foreground">
                            (opcional — vacío queda a tu nombre)
                        </span>
                    </label>
                    <Input
                        id="exc-requested-by"
                        {...register('requestedBy')}
                        placeholder="Identificador del vendedor que la pidió"
                    />
                    {errors.requestedBy ? (
                        <p className={ERROR}>{errors.requestedBy.message}</p>
                    ) : null}
                </div>

                <div>
                    <label className={FIELD_LABEL} htmlFor="exc-justification">
                        Justificación <span className="text-destructive">*</span>
                    </label>
                    <textarea
                        id="exc-justification"
                        rows={5}
                        aria-required
                        aria-invalid={errors.justification ? true : undefined}
                        {...register('justification')}
                        placeholder="Por qué se necesita romper la regla en este caso. Lo va a leer quien apruebe y quien audite."
                        className={TEXTAREA}
                    />
                    <div className="mt-1 flex items-baseline justify-between gap-2">
                        {errors.justification ? (
                            <p className={ERROR}>{errors.justification.message}</p>
                        ) : (
                            <p className="text-[11px] text-muted-foreground">
                                Es el único rastro de por qué se autorizó. Sin esto, la excepción no
                                es auditable.
                            </p>
                        )}
                        <span className="flex-none text-[10.5px] tabular-nums text-muted-foreground">
                            {justification.trim().length}/{MIN_JUSTIFICATION}
                        </span>
                    </div>
                </div>

                <div>
                    <label className={FIELD_LABEL} htmlFor="exc-action">
                        Acción propuesta <span className="text-destructive">*</span>
                    </label>
                    <textarea
                        id="exc-action"
                        rows={3}
                        aria-required
                        aria-invalid={errors.requestedAction ? true : undefined}
                        {...register('requestedAction')}
                        placeholder="Qué hay que hacer si se aprueba"
                        className={TEXTAREA}
                    />
                    {errors.requestedAction ? (
                        <p className={ERROR}>{errors.requestedAction.message}</p>
                    ) : null}
                </div>

                <p className="rounded-md bg-muted/50 p-2.5 text-[11px] leading-snug text-muted-foreground">
                    <strong className="text-foreground">Nota:</strong> la excepción se crea en
                    estado <strong className="text-foreground">Nueva</strong>. Requiere revisión y
                    aprobación antes de aplicarse, y todo queda en la bitácora de auditoría.
                </p>

                <div className="flex justify-end gap-2 border-t border-border pt-3">
                    <Button type="button" variant="outline" onClick={close}>
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={isSubmitting}>
                        Levantar excepción
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

export function ResolveExceptionModal({
    exception,
    approve,
    onClose,
    onSubmit,
    isSubmitting,
}: {
    exception: ExceptionDetail | null;
    approve: boolean;
    onClose: () => void;
    onSubmit: (values: ResolveExceptionForm) => void;
    isSubmitting: boolean;
}) {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<ResolveExceptionForm>({
        resolver: zodResolver(resolveExceptionSchema),
        defaultValues: { resolution: '' },
    });

    const close = () => {
        reset();
        onClose();
    };

    const resolution = watch('resolution');

    return (
        <Modal
            isOpen={exception !== null}
            onClose={close}
            title={approve ? 'Aprobar excepción' : 'Rechazar excepción'}
            size="md"
        >
            {exception ? (
                <form
                    onSubmit={(event) => {
                        void handleSubmit((values) => {
                            onSubmit(values);
                            reset();
                        })(event);
                    }}
                    className="flex flex-col gap-3"
                >
                    <p className="text-[12px] leading-snug">
                        {exception.justification.split('\n', 1)[0].trim()}
                    </p>

                    <div>
                        <label className={FIELD_LABEL} htmlFor="exc-resolution">
                            Resolución <span className="text-destructive">*</span>
                        </label>
                        <textarea
                            id="exc-resolution"
                            rows={4}
                            aria-required
                            aria-invalid={errors.resolution ? true : undefined}
                            {...register('resolution')}
                            placeholder={
                                approve
                                    ? 'Con qué fundamento se aprueba y bajo qué condiciones.'
                                    : 'Por qué se rechaza y qué haría falta para volver a levantarla.'
                            }
                            className={TEXTAREA}
                        />
                        <div className="mt-1 flex items-baseline justify-between gap-2">
                            {errors.resolution ? (
                                <p className={ERROR}>{errors.resolution.message}</p>
                            ) : (
                                <p className="text-[11px] text-muted-foreground">
                                    Queda en la bitácora de auditoría como el fundamento de la
                                    decisión.
                                </p>
                            )}
                            <span className="flex-none text-[10.5px] tabular-nums text-muted-foreground">
                                {resolution.trim().length}/{MIN_RESOLUTION}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-border pt-3">
                        <Button type="button" variant="outline" onClick={close}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant={approve ? 'default' : 'destructive'}
                            isLoading={isSubmitting}
                        >
                            {approve ? 'Aprobar' : 'Rechazar'}
                        </Button>
                    </div>
                </form>
            ) : null}
        </Modal>
    );
}
